import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Mail, Trash2, RefreshCw, ShieldCheck, Send, KeyRound, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { sendAdminResetLink } from '../lib/passwordResetHelper';

const API = process.env.REACT_APP_BACKEND_URL;

const PERMISSIONS = [
  { key: 'recruiters', label: 'Recruiters', desc: 'Manage recruiters, send agreements, activate' },
  { key: 'colleges', label: 'Colleges & Commissions', desc: 'Add colleges, configure commission rates' },
  { key: 'students', label: 'Student Applications', desc: 'Review and assign student applications' },
  { key: 'leads', label: 'Lead Management', desc: 'Triage marketing leads and follow-ups' },
];

// Granular module catalog used by the permission matrix grid (module × action).
const MODULES = [
  { key: 'students',    label: 'Student Applications', desc: 'Review and process student applications' },
  { key: 'recruiters',  label: 'Recruiters',           desc: 'Manage recruiter onboarding & agreements' },
  { key: 'colleges',    label: 'Colleges & Programs',  desc: 'Add colleges, programs & commission rates' },
  { key: 'leads',       label: 'Lead Management',      desc: 'Triage marketing leads & follow-ups' },
  { key: 'commissions', label: 'Commissions (Financial)', desc: 'See & edit commission earnings — sensitive' },
  { key: 'messages',    label: 'Messages',             desc: 'Internal chat with team & students' },
  { key: 'documents',   label: 'Documents Archive',    desc: 'Browse / export uploaded documents' },
  { key: 'activity_log',label: 'Activity Log',         desc: 'View audit trail of platform actions', actions: ['view'] },
];
const ALL_ACTIONS = ['view', 'edit', 'delete'];
const ACTION_LABEL = { view: 'View', edit: 'Edit', delete: 'Delete' };

const STAFF_ROLES = [
  {
    key: 'manager',
    label: 'Manager',
    desc: 'Sees all data across the org, but cannot create/manage staff or view system-level settings.',
  },
  {
    key: 'counsellor',
    label: 'Counsellor',
    desc: 'Sees only leads / recruiters / students that an admin or manager has explicitly assigned to them.',
  },
];

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', staff_role: 'counsellor', permissions: [] });
  const [error, setError] = useState('');
  const [presets, setPresets] = useState({});

  // Email lookup / cleanup tool
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');

  useEffect(() => {
    fetchStaff();
    fetchCatalog();
  }, []);

  async function fetchCatalog() {
    try {
      const res = await fetch(`${API}/api/staff/permission-catalog`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setPresets(d.presets || {});
      }
    } catch {/* ignore */}
  }

  // Apply a preset bundle (e.g. counsellor / manager / front_desk) onto the form.
  function applyPreset(name) {
    const perms = presets[name];
    if (!perms) return;
    setForm((f) => ({ ...f, permissions: perms }));
  }

  // Toggle one specific module.action permission.
  function togglePerm(modKey, action) {
    const key = `${modKey}.${action}`;
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  }

  // When user changes the role radio, auto-apply that role's preset bundle.
  function setRole(roleKey) {
    setForm((f) => ({ ...f, staff_role: roleKey, permissions: presets[roleKey] || f.permissions }));
  }

  async function fetchStaff() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/staff`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setStaff(d.staff || []);
      }
    } catch {/* ignore */} finally { setLoading(false); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/admin/staff`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Create failed');
      setDialogOpen(false);
      setForm({ name: '', email: '', staff_role: 'counsellor', permissions: [] });
      fetchStaff();
      if (d.email_sent === false) {
        alert(`⚠️ Staff created in the database, but the invitation email could NOT be sent.\n\nReason: ${d.email_error || 'Unknown'}\n\nFix: ensure RESEND_API_KEY and SENDER_EMAIL are set in your production environment, then click "Resend invitation" on this staff row.`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTogglePerm(s, key) {
    const next = s.permissions.includes(key)
      ? s.permissions.filter((p) => p !== key)
      : [...s.permissions, key];
    try {
      const res = await fetch(`${API}/api/admin/staff/${s.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: next }),
      });
      if (!res.ok) throw new Error('Update failed');
      fetchStaff();
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleResend(s) {
    try {
      const res = await fetch(`${API}/api/admin/staff/${s.id}/resend-invite`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Resend failed');
      alert(`Invitation re-sent to ${s.email}`);
      fetchStaff();
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleDelete(s) {
    if (!window.confirm(`Permanently remove ${s.name} (${s.email})? They will lose access immediately.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/staff/${s.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      fetchStaff();
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleLookup() {
    setLookupError('');
    setLookupResult(null);
    const email = lookupEmail.trim().toLowerCase();
    if (!email) { setLookupError('Enter an email first.'); return; }
    setLookupLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/lookup-email?email=${encodeURIComponent(email)}`, { credentials: 'include' });
      if (res.status === 401) {
        setLookupError('Your session has expired. Please refresh the page or sign in again.');
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Lookup failed');
      setLookupResult(data);
    } catch (err) {
      setLookupError(err.message);
    } finally {
      setLookupLoading(false);
    }
  }

  async function handleForceCleanup() {
    if (!lookupResult || lookupResult.is_free) return;
    const email = lookupResult.email;
    if (!window.confirm(`PERMANENTLY remove every trace of ${email} from this system?\n\nThis cannot be undone. It will free the email for new sign-ups.`)) return;
    setLookupLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/force-cleanup-email?email=${encodeURIComponent(email)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.status === 401) {
        setLookupError('Your session has expired. Please refresh the page or sign in again.');
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Cleanup failed');
      alert(`Removed: users=${data.deleted.users}, recruiters=${data.deleted.recruiters}, students=${data.deleted.student_profiles}, reset_tokens=${data.deleted.password_reset_tokens}`);
      setLookupResult({ ...lookupResult, is_free: true, user: null, recruiter: null, student_profile: null });
      fetchStaff();
    } catch (err) {
      setLookupError(err.message);
    } finally {
      setLookupLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: 'Total staff', v: staff.length, c: 'text-tge-navy' },
          { l: 'Active', v: staff.filter((s) => s.is_active).length, c: 'text-emerald-600' },
          { l: 'Pending invites', v: staff.filter((s) => s.activation_pending).length, c: 'text-amber-600' },
          { l: 'Modules covered', v: new Set(staff.flatMap((s) => s.permissions || [])).size, c: 'text-tge-gold' },
        ].map((s, i) => (
          <motion.div key={s.l} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="bg-white rounded-2xl p-5 border border-tge-navy/5">
            <div className="text-[11px] uppercase tracking-wider text-tge-muted">{s.l}</div>
            <div className={`font-serif-display text-3xl mt-2 ${s.c}`}>{s.v}</div>
          </motion.div>
        ))}
      </div>

      {/* Email lookup / cleanup */}
      <div className="bg-white rounded-2xl border border-tge-navy/5 p-5" data-testid="email-lookup-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-tge-gold/15 flex items-center justify-center shrink-0">
            <Search size={16} className="text-tge-navy" />
          </div>
          <div className="flex-1">
            <h3 className="font-serif-display text-lg text-tge-navy leading-tight">Find an account by email</h3>
            <p className="text-[12px] text-tge-muted mt-0.5">
              Use this if a sign-up says "email already in use" but you can't find that person anywhere.
              Often it's an orphaned record from a deleted staff/recruiter.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            placeholder="someone@example.com"
            value={lookupEmail}
            onChange={(e) => { setLookupEmail(e.target.value); setLookupError(''); }}
            data-testid="email-lookup-input"
          />
          <Button type="button" onClick={handleLookup} disabled={lookupLoading || !lookupEmail.trim()} className="bg-tge-navy hover:bg-tge-navy-deep text-white shrink-0" data-testid="email-lookup-search">
            {lookupLoading ? 'Searching…' : 'Search'}
          </Button>
        </div>
        {lookupError && <div className="mt-2 text-xs text-rose-600">{lookupError}</div>}
        {lookupResult && (
          <div className="mt-3 rounded-xl border border-tge-cream bg-tge-cream-soft/30 p-3 text-xs space-y-1" data-testid="email-lookup-result">
            <div><strong>{lookupResult.email}</strong></div>
            {lookupResult.is_free ? (
              <div className="text-emerald-700">✓ This email is free — you can create a new account with it.</div>
            ) : (
              <>
                {lookupResult.user && (
                  <div>👤 <strong>User</strong>: role=<code>{lookupResult.user.role}</code>, name={lookupResult.user.name || '(none)'}, active={String(lookupResult.user.is_active)}</div>
                )}
                {lookupResult.recruiter && (
                  <div>🤝 <strong>Recruiter profile</strong>: id={lookupResult.recruiter.recruiter_id}, company={lookupResult.recruiter.company_name}, status={lookupResult.recruiter.status}</div>
                )}
                {lookupResult.student_profile && (
                  <div>🎓 <strong>Student profile</strong>: id={lookupResult.student_profile.student_id}, name={lookupResult.student_profile.student_name}</div>
                )}
                <div className="pt-2">
                  <Button type="button" size="sm" onClick={handleForceCleanup} disabled={lookupLoading} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="email-lookup-cleanup">
                    Force-remove all traces of this email
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Stale-assignments sweeper */}
        <div className="mt-4 pt-4 border-t border-tge-cream flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-tge-muted leading-relaxed">
              Seeing a deleted staff name in an "Assigned to" dropdown?
              <br />Run this to unassign every reference to deleted accounts.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!window.confirm('Clear all stale staff assignments?')) return;
                try {
                  const res = await fetch(`${API}/api/admin/cleanup-stale-assignments`, { method: 'POST', credentials: 'include' });
                  const data = await res.json();
                  if (!res.ok) { alert(data.detail || 'Failed'); return; }
                  alert(`Cleared. Leads: ${data.fixed.leads}, Recruiters: ${data.fixed.recruiters}, Students: ${data.fixed.students}`);
                } catch (err) { alert(err.message); }
              }}
              data-testid="cleanup-stale-assignments"
              className="shrink-0"
            >
              Clean stale assignments
            </Button>
          </div>

          <div className="flex items-center justify-between gap-3 pt-3 border-t border-tge-cream">
            <p className="text-[11px] text-tge-muted leading-relaxed">
              <strong>Migrate files to cloud storage.</strong> One-click: copies every existing
              student / recruiter / chat file from the server's local disk into Cloudflare R2,
              so they survive every redeploy. Safe to re-run anytime.
            </p>
            <Button
              type="button"
              size="sm"
              onClick={async () => {
                if (!window.confirm('Migrate all uploaded files from local disk → Cloudflare R2?\n\nThis is safe to re-run.')) return;
                try {
                  const res = await fetch(`${API}/api/admin/storage/migrate-to-cloud`, { method: 'POST', credentials: 'include' });
                  const data = await res.json();
                  if (!res.ok) { alert(data.detail || 'Failed'); return; }
                  alert(`Migration complete!\n\nStudents: ${data.migrated.students}\nRecruiters: ${data.migrated.recruiters}\nChat attachments: ${data.migrated.messages}\nMissing on disk: ${data.migrated.skipped_missing}`);
                } catch (err) { alert(err.message); }
              }}
              data-testid="migrate-to-cloud"
              className="bg-tge-navy hover:bg-tge-navy-deep text-white shrink-0"
            >
              Migrate files to cloud
            </Button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden">
        <div className="px-6 py-5 border-b border-tge-navy/5 flex items-center justify-between">
          <h3 className="font-serif-display text-xl text-tge-navy">Staff members</h3>
          <div className="flex items-center gap-2">
            <button onClick={fetchStaff} className="inline-flex items-center gap-1.5 text-xs text-tge-muted hover:text-tge-navy transition px-3 py-1.5 rounded-full border border-tge-navy/10 hover:border-tge-navy/30 hover:bg-tge-cream-soft" data-testid="staff-refresh-btn">
              <RefreshCw size={12} /> Refresh
            </button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-tge-navy hover:bg-tge-navy-deep text-white" data-testid="staff-create-btn">
                  <Plus size={14} className="mr-1" /> Add staff
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-tge-cream/60 shrink-0">
                  <DialogTitle>Invite new staff member</DialogTitle>
                  <DialogDescription>They'll receive an email with a link to set their password.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate} className="flex flex-col flex-1 min-h-0">
                  <div className="space-y-4 px-6 py-5 overflow-y-auto flex-1">
                  {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded-lg text-sm">{error}</div>}
                  <div>
                    <Label>Full name *</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" data-testid="staff-form-name" />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@trueglobaleducation.com" data-testid="staff-form-email" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-tge-gold" /> Access tier</Label>
                    <p className="text-xs text-tge-muted mt-0.5 mb-2">Pick the level of data access this person should have.</p>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {STAFF_ROLES.map((r) => (
                        <label key={r.key}
                          className={`p-3 rounded-xl border-2 cursor-pointer transition ${form.staff_role === r.key ? 'border-tge-navy bg-tge-cream-soft' : 'border-tge-cream hover:border-tge-gold/40'}`}
                          data-testid={`staff-form-role-${r.key}`}>
                          <input type="radio" name="staff_role" className="sr-only" checked={form.staff_role === r.key} onChange={() => setRole(r.key)} />
                          <div className="text-sm font-semibold text-tge-navy flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${form.staff_role === r.key ? 'bg-tge-gold' : 'bg-tge-cream'}`} />
                            {r.label}
                          </div>
                          <div className="text-[11px] text-tge-muted mt-1 leading-relaxed">{r.desc}</div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-tge-gold" /> Permission matrix</Label>
                      <div className="flex items-center gap-2 text-[11px]">
                        <span className="text-tge-muted">Quick set:</span>
                        {Object.keys(presets).map((p) => (
                          <button
                            key={p} type="button" onClick={() => applyPreset(p)}
                            className="px-2 py-0.5 rounded-full border border-tge-cream hover:border-tge-gold text-tge-navy capitalize"
                            data-testid={`staff-form-preset-${p}`}
                          >
                            {p.replace('_', ' ')}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-tge-muted mb-3">Fine-tune what this staff member can do. Admin & Manager bypass these gates automatically.</p>
                    <div className="rounded-xl border border-tge-cream overflow-hidden">
                      <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 px-3 py-2 bg-tge-cream-soft/60 text-[10px] uppercase tracking-wider text-tge-muted font-semibold">
                        <span>Module</span>
                        <span className="w-12 text-center">View</span>
                        <span className="w-12 text-center">Edit</span>
                        <span className="w-12 text-center">Delete</span>
                      </div>
                      {MODULES.map((m) => {
                        const actions = m.actions || ALL_ACTIONS;
                        return (
                          <div key={m.key} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 px-3 py-2 border-t border-tge-cream/70 hover:bg-tge-cream-soft/40">
                            <div>
                              <div className="text-sm font-medium text-tge-navy">{m.label}</div>
                              <div className="text-[11px] text-tge-muted leading-tight">{m.desc}</div>
                            </div>
                            {ALL_ACTIONS.map((a) => (
                              <div key={a} className="w-12 text-center">
                                {actions.includes(a) ? (
                                  <input
                                    type="checkbox"
                                    className="accent-tge-navy w-4 h-4 cursor-pointer"
                                    checked={form.permissions.includes(`${m.key}.${a}`)}
                                    onChange={() => togglePerm(m.key, a)}
                                    aria-label={`${ACTION_LABEL[a]} ${m.label}`}
                                    data-testid={`staff-form-perm-${m.key}-${a}`}
                                  />
                                ) : (
                                  <span className="text-tge-cream">—</span>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  </div>
                  <div className="px-6 py-4 border-t border-tge-cream/60 bg-white shrink-0">
                  <Button type="submit" disabled={submitting} className="w-full bg-tge-navy hover:bg-tge-navy-deep" data-testid="staff-form-submit">
                    {submitting ? 'Sending invite…' : <><Send size={14} className="mr-1.5" /> Send invitation</>}
                  </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-tge-muted text-sm">Loading…</div>
        ) : staff.length === 0 ? (
          <div className="text-center py-16 text-tge-muted">
            <p>No staff members yet. Click <strong>Add staff</strong> to invite your first teammate.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-tge-cream-soft">
                <tr>
                  {['Member', 'Tier', 'Status', 'Permissions', 'Actions'].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-[11px] font-semibold text-tge-muted uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-tge-cream">
                {staff.map((s) => (
                  <tr key={s.id} className="hover:bg-tge-cream-soft/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-tge-gold/20 text-tge-gold font-serif-display flex items-center justify-center">
                          {s.name?.charAt(0)?.toUpperCase() || 'S'}
                        </div>
                        <div>
                          <div className="font-medium text-tge-navy text-sm">{s.name}</div>
                          <div className="text-xs text-tge-muted">{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${s.role === 'manager' ? 'bg-violet-100 text-violet-800' : 'bg-sky-100 text-sky-800'}`}>
                        {s.role === 'manager' ? 'Manager' : 'Counsellor'}
                      </span>
                      <div className="text-[10px] text-tge-muted mt-1">
                        {s.scope === 'all' ? 'Sees all data' : 'Assigned only'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {s.activation_pending ? (
                        <Badge className="bg-amber-100 text-amber-800 border-0">Pending invite</Badge>
                      ) : s.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-0">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700 border-0">Disabled</Badge>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <PermissionSummary perms={s.permissions} role={s.role} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {s.activation_pending && (
                          <button onClick={() => handleResend(s)} className="inline-flex items-center gap-1 text-[11px] text-tge-navy hover:text-tge-gold transition px-2 py-1 rounded-full border border-tge-navy/10 hover:border-tge-gold/50" data-testid={`staff-resend-${s.id}`}>
                            <Mail size={11} /> Resend
                          </button>
                        )}
                        {!s.activation_pending && s.is_active && (
                          <button
                            onClick={() => sendAdminResetLink({ user_id: s.id, email: s.email, displayName: s.name })}
                            className="inline-flex items-center gap-1 text-[11px] text-tge-navy hover:text-tge-gold transition px-2 py-1 rounded-full border border-tge-navy/10 hover:border-tge-gold/50"
                            data-testid={`staff-reset-${s.id}`}
                            title="Email a password-reset link"
                          >
                            <KeyRound size={11} /> Reset
                          </button>
                        )}
                        <button onClick={() => handleDelete(s)} className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded-md transition" title="Remove staff" data-testid={`staff-delete-${s.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact summary of a staff member's permissions for the row UI. Groups by
// module so a counsellor with view+edit+delete on students shows as
// "Students (V·E·D)" instead of three pills.
function PermissionSummary({ perms = [], role }) {
  if (role === 'admin') {
    return <span className="text-[11px] text-tge-muted italic">Full system access</span>;
  }
  if (role === 'manager') {
    return <span className="text-[11px] text-violet-700 italic">All modules · all actions</span>;
  }
  if (!perms.length) {
    return <span className="text-[11px] text-tge-muted italic">No access — edit to grant</span>;
  }
  // Group module → set of action letters
  const grouped = {};
  for (const p of perms) {
    const [mod, action] = p.split('.');
    if (!action) continue;
    grouped[mod] = grouped[mod] || new Set();
    grouped[mod].add(action[0].toUpperCase()); // V / E / D
  }
  const order = ['students', 'recruiters', 'colleges', 'leads', 'commissions', 'messages', 'documents', 'activity_log'];
  const friendly = {
    students: 'Students', recruiters: 'Recruiters', colleges: 'Colleges',
    leads: 'Leads', commissions: 'Commissions', messages: 'Messages',
    documents: 'Documents', activity_log: 'Activity Log',
  };
  return (
    <div className="flex flex-wrap gap-1 max-w-md">
      {order.filter((k) => grouped[k]).map((k) => {
        const letters = ['V', 'E', 'D'].filter((l) => grouped[k].has(l)).join('·');
        const has_delete = grouped[k].has('D');
        const tone = has_delete ? 'bg-rose-100 text-rose-800 border-rose-200' : 'bg-tge-cream-soft text-tge-navy border-tge-cream';
        return (
          <span key={k} className={`text-[10px] px-2 py-0.5 rounded-full border ${tone}`} title={`${friendly[k]} — ${letters}`}>
            {friendly[k]} <span className="opacity-70 ml-1">{letters}</span>
          </span>
        );
      })}
    </div>
  );
}
